import {recordUploadProgress} from './eta/upload-eta-registry';
import {UploadType} from '@app/site-config';
import {PartialUploadType} from '@common/core/base-backend-bootstrap-data';
import {DEFAULT_CHUNK_SIZE} from '@common/core/settings/base-backend-settings';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {toast} from '@shadcn/toast/toast';
import {Trans} from '@ui/i18n/trans';
import {UploadedFile} from '@ui/utils/files/uploaded-file';
import {validateFile} from '@ui/utils/files/validate-file';
import {FileUpload, FileUploadState} from './file-upload-store';
import {ProgressTimeout} from './progress-timeout';
import {AxiosUpload} from './strategy/axios-upload';
import {S3MultipartUpload} from './strategy/s3-multipart-upload';
import {S3Upload} from './strategy/s3-upload';
import {TusUpload} from './strategy/tus-upload';
import {
  UploadStrategy,
  UploadStrategyConfigWithBackend,
} from './strategy/upload-strategy';

export async function startUploading(
  upload: FileUpload,
  state: FileUploadState,
): Promise<UploadStrategy | null> {
  const settings = getBootstrapData().settings;
  const options = upload.options;
  const file = upload.file as UploadedFile;

  // validate file, if validation fails, error the upload and bail
  if (options?.restrictions) {
    const errorMessage = validateFile(file, options.restrictions);
    if (errorMessage) {
      state.updateFileUpload(file.id, {
        errorMessage,
        status: 'failed',
        request: undefined,
        timer: undefined,
      });

      if (options.showToastOnRestrictionFail) {
        toast.error(<Trans {...errorMessage} />);
      }

      state.runQueue();
      return null;
    }
  }

  const backend = chooseBackend(
    options.uploadType,
    file.size,
    options.backendId,
  );

  if (!backend) {
    throw new Error('No backend found for upload type ' + options.uploadType);
  }

  if (options.backendId !== backend.id) {
    state.updateFileUpload(file.id, {
      options: {...options, backendId: backend.id},
    });
  }

  // prepare config for file upload strategy
  const timer = new ProgressTimeout(file.size > 4 * 1024 * 1024 ? 120000 : 30000);
  const config: UploadStrategyConfigWithBackend = {
    uploadType: options?.uploadType,
    backendId: backend.id,
    metadata: {
      ...options?.metadata,
      relativePath: file.relativePath,
      parentId: options?.metadata?.parentId || '',
    },
    chunkSize: settings.uploading?.chunk_size ?? DEFAULT_CHUNK_SIZE,
    baseUrl: settings.base_url,
    onError: errorMessage => {
      const current = state.getUpload(file.id);
      if (!current) {
        // Cancelled and removed from the queue.
        timer.done();
        return;
      }
      if (
        current.status === 'aborted' ||
        (typeof navigator !== 'undefined' && navigator.onLine === false)
      ) {
        state.updateFileUpload(file.id, {
          status: 'aborted',
          errorMessage: null,
          request: undefined,
        });
        timer.done();
        return;
      }
      // Network error without a server message: retry with backoff.
      const retries = current.autoRetries ?? 0;
      if (!errorMessage && retries < AUTO_RETRY_DELAYS.length) {
        timer.done();
        state.updateFileUpload(file.id, {
          status: 'aborted',
          errorMessage: null,
          autoRetries: retries + 1,
          request: undefined,
        });
        window.setTimeout(() => {
          const latest = state.getUpload(file.id);
          // Only if nobody paused/cancelled it meanwhile.
          if (latest?.status === 'aborted' && !latest.pausedByUser) {
            state.retryUpload(file.id);
          }
        }, AUTO_RETRY_DELAYS[retries]);
        return;
      }
      state.updateFileUpload(file.id, {
        errorMessage,
        status: 'failed',
      });
      state.runQueue();
      timer.done();
      options?.onError?.(errorMessage, file);
      if (options.showToastOnBackendError && errorMessage) {
        toast.error(errorMessage);
      }
    },
    onSuccess: entry => {
      if (!state.getUpload(file.id)) {
        timer.done();
        return;
      }
      state.updateFileUpload(file.id, {
        status: 'completed',
        entry,
        request: undefined,
        percentage: 100,
        bytesUploaded: file.size,
      });
      state.runQueue();
      timer.done();
      options?.onSuccess?.(entry, file);
    },
    onProgress: ({bytesUploaded, bytesTotal}) => {
      const current = state.getUpload(file.id);
      if (!current || current.status !== 'inProgress') return;
      recordUploadProgress(file.id, bytesUploaded);
      const percentage = bytesTotal ? (bytesUploaded / bytesTotal) * 100 : 0;
      state.updateFileUpload(file.id, {
        percentage,
        bytesUploaded,
      });
      timer.progress();
      options?.onProgress?.({bytesUploaded, bytesTotal});
    },
  };

  // choose and create upload strategy, based on file size and settings
  const strategy = chooseUploadStrategy(file, backend);
  const request = await strategy.create(file, config);

  // add handler for when upload times out (no progress for 30+ seconds)
  timer.timeoutHandler = () => {
    if (typeof document !== 'undefined' && document.hidden) {
      timer.progress();
      return;
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      timer.progress();
      return;
    }
    // Stalled: pause (keeps server bytes) and retry through the backoff path.
    void request.abort();
    config.onError?.(null, file);
  };

  const latest = state.getUpload(file.id);
  if (!latest || latest.status !== 'inProgress') {
    // Paused or cancelled while the strategy was being prepared.
    timer.done();
    return null;
  }
  state.updateFileUpload(file.id, {
    status: 'inProgress',
    request,
  });
  request.start();

  return request;
}

// Automatic retry delays after a network error (then the row shows Failed).
const AUTO_RETRY_DELAYS = [2000, 5000, 10000];

const OneMB = 1024 * 1024;
const FourMB = 4 * OneMB;
const HundredMB = 100 * OneMB;

const chooseUploadStrategy = (
  file: UploadedFile,
  backend: PartialUploadType['backends'][number],
) => {
  if (backend.driver === 's3' && backend.direct_upload) {
    return file.size >= HundredMB ? S3MultipartUpload : S3Upload;
  } else {
    // 4MB = Axios, otherwise Tus
    return file.size >= FourMB &&
      !getBootstrapData().settings.uploading?.disable_tus
      ? TusUpload
      : AxiosUpload;
  }
};

function chooseBackend(
  uploadType: keyof typeof UploadType,
  fileSize = 0,
  preferredId?: string,
) {
  const uploadingTypes = getBootstrapData().uploading_types;
  if (uploadingTypes && uploadType) {
    const backends = uploadingTypes[uploadType]?.backends ?? [];
    if (preferredId) {
      const preferred = backends.find(backend => backend.id === preferredId);
      if (preferred) {
        return preferred;
      }
    }
    const withRoom = backends.find(backend => {
      // null = Unlimited; 0 = accepts no new uploads
      if (backend.capacity_bytes == null) return true;
      return (backend.used_bytes ?? 0) + fileSize <= backend.capacity_bytes;
    });
    return withRoom ?? backends[backends.length - 1] ?? null;
  }
  return null;
}
